import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../config/supabase';
import { X, Upload, Loader2, Globe, ChevronDown, ChevronUp } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useForm } from 'react-hook-form';
import { traderService, TraderFeatures } from '../../services/traderService';
import { SocialLinks, getSocialLinksByUserId } from '../../services/socialLinksService';
import { compressImage, AVATAR_PRESET, GALLERY_PRESET, validateImageFile, formatFileSize } from '../../utils/imageCompression';

export interface Professional {
  id: number;
  user_id?: string;
  store_name: string;
  owner_name: string;
  email: string;
  phone_number: string;
  location: string;
  description: string;
  profession_id: number;
  avatar_url?: string | null;
  website?: string | null;
  verified: boolean;
  mockup?: boolean;
  created_at: string;
  updated_at: string;
  keywords?: string[];
}

interface EditProfessionalFormProps {
  professional: Professional;
  onClose: () => void;
  onSave: (updated: Professional) => void;
}

const EditProfessionalForm: React.FC<EditProfessionalFormProps> = ({ professional, onClose, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(professional?.avatar_url || null);
  const avatarPreviewObjectUrlRef = useRef<string | null>(null);
  const [professions, setProfessions] = useState<{ id: number; name: string; description?: string }[]>([]);
  
  // Social links state
  const [socialLinks, setSocialLinks] = useState<SocialLinks | null>(null);
  const [savingSocialLinks, setSavingSocialLinks] = useState(false);
  
  // Features state
  const [features, setFeatures] = useState<TraderFeatures | null>(null);
  const [savingFeatures, setSavingFeatures] = useState(false);
  const [featureImageFiles, setFeatureImageFiles] = useState<{
    feature1: File | null;
    feature2: File | null;
    feature3: File | null;
  }>({
    feature1: null,
    feature2: null,
    feature3: null
  });
  const [isCompressingAvatar, setIsCompressingAvatar] = useState(false);
  const [avatarCompressionInfo, setAvatarCompressionInfo] = useState<string | null>(null);
  const [featureImagePreviews, setFeatureImagePreviews] = useState<{
    feature1: string | null;
    feature2: string | null;
    feature3: string | null;
  }>({
    feature1: null,
    feature2: null,
    feature3: null
  });
  
  // Collapsible sections state
  const [expandedSections, setExpandedSections] = useState({
    basicInfo: true,
    socialLinks: false,
    features: false
  });
  
  // Toggle section expansion
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  useEffect(() => {
    const fetchProfessions = async () => {
      try {
        const { data, error } = await supabase
          .from('professionals_categories')
          .select('id, name, description')
          .order('name');

        if (error) {
          console.error('Error fetching professionals professions:', error);
          return;
        }

        if (data) {
          setProfessions(data);
        }
      } catch (err) {
        console.error('Failed to fetch professionals professions:', err);
      }
    };

    fetchProfessions();
  }, []);

  useEffect(() => {
    return () => {
      if (avatarPreviewObjectUrlRef.current) {
        URL.revokeObjectURL(avatarPreviewObjectUrlRef.current);
        avatarPreviewObjectUrlRef.current = null;
      }
    };
  }, []);
  
  // Fetch social links and features if professional has user_id
  useEffect(() => {
    const fetchAdditionalData = async () => {
      if (!professional.user_id) return;
      
      try {
        // Fetch social links
        const links = await getSocialLinksByUserId(professional.user_id);
        setSocialLinks(links);
        
        // Fetch features (using trader features table which works for all business types)
        const featuresData = await traderService.getTraderFeatures(professional.user_id);
        if (featuresData) {
          setFeatures(featuresData);
        } else {
          // Set default features
          setFeatures({
            user_id: professional.user_id,
            feature1_enabled: true,
            feature1_title: professional.store_name || 'Our Services',
            feature1_description: professional.description || 'Discover our professional services',
            feature1_image: professional.avatar_url || '/bally-Market-sign-200px.png',
            feature2_enabled: false,
            feature2_title: '',
            feature2_description: '',
            feature2_image: '',
            feature3_enabled: false,
            feature3_title: '',
            feature3_description: '',
            feature3_image: '',
            features_section_title: 'Our Services',
            features_layout: 'rows'
          });
        }
      } catch (err) {
        console.error('Error fetching additional professional data:', err);
      }
    };
    
    fetchAdditionalData();
  }, [professional.user_id, professional.store_name, professional.description, professional.avatar_url]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    reset,
  } = useForm<Professional>({
    defaultValues: {
      ...professional,
      profession_id: professional.profession_id,
    },
    mode: 'onChange',
  });

  useEffect(() => {
    reset({
      ...professional,
      profession_id: professional.profession_id,
    });
    setAvatarFile(null);
    if (avatarPreviewObjectUrlRef.current) {
      URL.revokeObjectURL(avatarPreviewObjectUrlRef.current);
      avatarPreviewObjectUrlRef.current = null;
    }
    setAvatarPreview(professional?.avatar_url || null);
  }, [professional.id, reset]);

  const onSubmit = async (data: Professional) => {
    if (loading || isSubmitting) return;

    try {
      setLoading(true);
      setError('');

      if (!data.store_name?.trim()) {
        setError('Store name is required');
        return;
      }

      let avatarUrl = professional?.avatar_url || null;
      if (avatarFile) {
        avatarUrl = await uploadAvatar();
      }

      const { data: updated, error: updateError } = await supabase
        .from('professionals')
        .update({
          store_name: data.store_name.trim(),
          owner_name: data.owner_name.trim(),
          email: data.email.trim(),
          phone_number: data.phone_number.trim(),
          location: data.location.trim(),
          description: data.description.trim(),
          profession_id: data.profession_id,
          website: data.website?.trim() || null,
          avatar_url: avatarUrl,
          updated_at: new Date().toISOString(),
        })
        .eq('id', professional.id)
        .select(`
          id,
          user_id,
          store_name,
          owner_name,
          email,
          phone_number,
          location,
          description,
          profession_id,
          website,
          avatar_url,
          verified,
          created_at,
          updated_at
        `)
        .single();

      if (updateError) {
        console.error('Error updating professional:', updateError);
        throw new Error(`Failed to update professional: ${updateError.message}`);
      }

      if (!updated) {
        throw new Error('No data returned after professional update');
      }

      onSave(updated as Professional);
      onClose();
    } catch (err) {
      console.error('Error updating professional:', err);
      setError(err instanceof Error ? err.message : 'Failed to update professional');
    } finally {
      setLoading(false);
    }
  };

  const uploadAvatar = async (): Promise<string> => {
    if (!avatarFile) return professional?.avatar_url || '';

    const fileExt = avatarFile.name.split('.').pop()?.toLowerCase();
    const fileName = `${uuidv4()}.${fileExt}`;
    const filePath = `professionals/${professional.id}/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(filePath, avatarFile, {
        cacheControl: '3600',
        upsert: true,
      });

    if (uploadError) {
      console.error('Error uploading professional avatar:', uploadError);
      throw uploadError;
    }

    const { data } = supabase.storage
      .from('avatars')
      .getPublicUrl(filePath);

    if (!data || !data.publicUrl) {
      throw new Error('Failed to get avatar URL');
    }

    return data.publicUrl;
  };

  const uploadFeatureImage = async (file: File, featureNum: number): Promise<string> => {
    const validation = validateImageFile(file, 20);
    if (!validation.isValid) {
      alert(validation.error ?? 'Please select a smaller image file.');
      throw new Error(validation.error ?? 'Invalid image');
    }

    const originalSize = file.size;
    const compressedFile = await compressImage(file, GALLERY_PRESET);
    const compressedSize = compressedFile.size;
    const reduction =
      originalSize > 0
        ? (((originalSize - compressedSize) / originalSize) * 100).toFixed(0)
        : '0';

    const fileExt = compressedFile.name.split('.').pop()?.toLowerCase() || 'jpg';
    const fileName = `feature${featureNum}_${uuidv4()}.${fileExt}`;
    const filePath = `professionals/${professional.id}/features/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(filePath, compressedFile, {
        cacheControl: '3600',
        upsert: true
      });

    if (uploadError) {
      throw uploadError;
    }

    const { data } = supabase.storage
      .from('avatars')
      .getPublicUrl(filePath);

    alert(
      `Feature image optimized: ${formatFileSize(originalSize)} → ${formatFileSize(
        compressedSize
      )} (${reduction}% smaller)`
    );

    return data.publicUrl;
  };

  const handleSaveFeatures = async () => {
    if (!features || !professional.user_id) return;
    setSavingFeatures(true);
    
    try {
      let updatedFeatures = { ...features };
      
      // Upload feature images if new files selected
      if (featureImageFiles.feature1) {
        updatedFeatures.feature1_image = await uploadFeatureImage(featureImageFiles.feature1, 1);
      }
      if (featureImageFiles.feature2) {
        updatedFeatures.feature2_image = await uploadFeatureImage(featureImageFiles.feature2, 2);
      }
      if (featureImageFiles.feature3) {
        updatedFeatures.feature3_image = await uploadFeatureImage(featureImageFiles.feature3, 3);
      }
      
      const { error: upsertError } = await supabase
        .from('trader_features')
        .upsert({
          ...updatedFeatures,
          user_id: professional.user_id,
          updated_at: new Date().toISOString()
        }, { onConflict: 'user_id' });
      
      if (upsertError) throw upsertError;
      
      setFeatures(updatedFeatures);
      setFeatureImageFiles({ feature1: null, feature2: null, feature3: null });
      alert('✅ Features saved successfully!');
    } catch (err) {
      console.error('Error saving features:', err);
      alert('Failed to save features');
    } finally {
      setSavingFeatures(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsCompressingAvatar(true);
      setAvatarCompressionInfo(null);

      const validation = validateImageFile(file, 20);
      if (!validation.isValid) {
        setError(validation.error ?? 'Please select a smaller image file.');
        return;
      }

      const originalSize = file.size;
      const compressedFile = await compressImage(file, AVATAR_PRESET);
      const compressedSize = compressedFile.size;
      const reduction =
        originalSize > 0
          ? (((originalSize - compressedSize) / originalSize) * 100).toFixed(0)
          : '0';

      setAvatarFile(compressedFile);
      if (avatarPreviewObjectUrlRef.current) {
        URL.revokeObjectURL(avatarPreviewObjectUrlRef.current);
        avatarPreviewObjectUrlRef.current = null;
      }
      const nextPreviewUrl = URL.createObjectURL(compressedFile);
      avatarPreviewObjectUrlRef.current = nextPreviewUrl;
      setAvatarPreview(nextPreviewUrl);
      setAvatarCompressionInfo(
        `Image optimized: ${formatFileSize(originalSize)} → ${formatFileSize(
          compressedSize
        )} (${reduction}% smaller)`
      );
    } catch (err) {
      console.error('Error processing avatar image:', err);
      setError('Failed to process avatar image. Please try another file.');
    } finally {
      setIsCompressingAvatar(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="p-6 flex-shrink-0">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {professional?.id ? 'Edit Professional' : 'Add New Professional'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="p-6 pt-0 overflow-y-auto flex-1">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-4">
              <div>
                <Label htmlFor="store_name">Business Name*</Label>
                <Input
                  id="store_name"
                  {...register('store_name', { required: 'Business name is required' })}
                  className={errors.store_name ? 'border-red-500' : ''}
                />
              </div>

              <div>
                <Label htmlFor="owner_name">Owner Name*</Label>
                <Input
                  id="owner_name"
                  {...register('owner_name', { required: 'Owner name is required' })}
                  className={errors.owner_name ? 'border-red-500' : ''}
                />
              </div>

              <div>
                <Label htmlFor="email">Email*</Label>
                <Input
                  id="email"
                  type="email"
                  {...register('email', { required: 'Email is required' })}
                  className={errors.email ? 'border-red-500' : ''}
                />
              </div>

              <div>
                <Label htmlFor="phone_number">Phone Number*</Label>
                <Input
                  id="phone_number"
                  {...register('phone_number', { required: 'Phone number is required' })}
                  className={errors.phone_number ? 'border-red-500' : ''}
                />
              </div>

              <div>
                <Label htmlFor="location">Location*</Label>
                <Input
                  id="location"
                  {...register('location', { required: 'Location is required' })}
                  className={errors.location ? 'border-red-500' : ''}
                />
              </div>

              <div>
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  type="url"
                  placeholder="https://"
                  {...register('website')}
                  className={errors.website ? 'border-red-500' : ''}
                />
              </div>

              <div>
                <Label htmlFor="description">Description*</Label>
                <Textarea
                  id="description"
                  {...register('description', { required: 'Description is required' })}
                  className={errors.description ? 'border-red-500' : ''}
                  rows={4}
                />
              </div>

              <div>
                <Label htmlFor="profession_id">Profession*</Label>
                <Select
                  onValueChange={value => setValue('profession_id', parseInt(value))}
                  defaultValue={String(professional?.profession_id)}
                >
                  <SelectTrigger className={errors.profession_id ? 'border-red-500' : ''}>
                    <SelectValue placeholder="Select a profession" />
                  </SelectTrigger>
                  <SelectContent>
                    {professions.map((profession) => (
                      <SelectItem
                        key={profession.id}
                        value={String(profession.id)}
                      >
                        {profession.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Avatar</Label>
                <div className="flex items-center space-x-4">
                  <img
                    src={avatarPreview || professional?.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${professional?.store_name}`}
                    alt="Avatar preview"
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <label
                    htmlFor="avatar-upload-professional"
                    className="cursor-pointer bg-blue-50 text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-100 flex items-center space-x-2"
                  >
                    <Upload className="w-4 h-4" />
                    <span>Upload new avatar</span>
                  </label>
                  <input
                    id="avatar-upload-professional"
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </div>
                {isCompressingAvatar && (
                  <p className="mt-1 text-xs text-gray-500">Optimizing image...</p>
                )}
                {avatarCompressionInfo && !isCompressingAvatar && (
                  <p className="mt-1 text-xs text-gray-600">{avatarCompressionInfo}</p>
                )}
              </div>

              {/* Social Links Section - Only show if professional has user_id */}
              {professional.user_id && (
                <div className="pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => toggleSection('socialLinks')}
                    className="flex items-center justify-between w-full text-left"
                  >
                    <Label className="flex items-center gap-2 cursor-pointer">
                      <Globe className="w-4 h-4" />
                      Social Media Links
                    </Label>
                    {expandedSections.socialLinks ? (
                      <ChevronUp className="w-4 h-4 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-500" />
                    )}
                  </button>
                  
                  {expandedSections.socialLinks && socialLinks && (
                    <div className="mt-3 p-4 bg-gray-50 rounded-lg space-y-4">
                      {/* Show socials toggle */}
                      <div className="flex items-center justify-between pb-3 border-b border-gray-200">
                        <div>
                          <p className="text-sm font-medium text-gray-700">Show social media section</p>
                          <p className="text-xs text-gray-500">Toggle visibility on public profile</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setSocialLinks(prev => prev ? { ...prev, show_socials: !prev.show_socials } : prev)}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-200 ${
                            socialLinks.show_socials !== false ? 'bg-blue-600' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
                              socialLinks.show_socials !== false ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                      
                      {/* Social links inputs */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Facebook</label>
                          <Input
                            type="url"
                            value={socialLinks.facebook_url || ''}
                            onChange={(e) => setSocialLinks(prev => prev ? { ...prev, facebook_url: e.target.value } : prev)}
                            placeholder="https://facebook.com/..."
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Instagram</label>
                          <Input
                            type="url"
                            value={socialLinks.instagram_url || ''}
                            onChange={(e) => setSocialLinks(prev => prev ? { ...prev, instagram_url: e.target.value } : prev)}
                            placeholder="https://instagram.com/..."
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">YouTube</label>
                          <Input
                            type="url"
                            value={socialLinks.youtube_url || ''}
                            onChange={(e) => setSocialLinks(prev => prev ? { ...prev, youtube_url: e.target.value } : prev)}
                            placeholder="https://youtube.com/..."
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">LinkedIn</label>
                          <Input
                            type="url"
                            value={socialLinks.linkedin_url || ''}
                            onChange={(e) => setSocialLinks(prev => prev ? { ...prev, linkedin_url: e.target.value } : prev)}
                            placeholder="https://linkedin.com/..."
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">TikTok</label>
                          <Input
                            type="url"
                            value={socialLinks.tiktok_url || ''}
                            onChange={(e) => setSocialLinks(prev => prev ? { ...prev, tiktok_url: e.target.value } : prev)}
                            placeholder="https://tiktok.com/..."
                            className="text-sm"
                          />
                        </div>
                      </div>
                      
                      {/* Save social links button */}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          if (!socialLinks || !professional.user_id) return;
                          setSavingSocialLinks(true);
                          try {
                            const { error: upsertError } = await supabase
                              .from('user_social_links')
                              .upsert({
                                ...socialLinks,
                                user_id: professional.user_id,
                                updated_at: new Date().toISOString()
                              }, { onConflict: 'user_id' });
                            
                            if (upsertError) throw upsertError;
                            alert('Social links saved successfully!');
                          } catch (err) {
                            console.error('Error saving social links:', err);
                            alert('Failed to save social links');
                          } finally {
                            setSavingSocialLinks(false);
                          }
                        }}
                        disabled={savingSocialLinks}
                        className="w-full"
                      >
                        {savingSocialLinks ? (
                          <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving...</>
                        ) : (
                          'Save Social Links'
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Features Section - Only show if professional has user_id */}
              {professional.user_id && (
                <div className="pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => toggleSection('features')}
                    className="flex items-center justify-between w-full text-left"
                  >
                    <Label className="flex items-center gap-2 cursor-pointer">
                      ⭐ Featured Services
                    </Label>
                    {expandedSections.features ? (
                      <ChevronUp className="w-4 h-4 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-500" />
                    )}
                  </button>
                  
                  {expandedSections.features && features && (
                    <div className="mt-3 p-4 bg-gray-50 rounded-lg space-y-4">
                      {/* Section Title */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Section Title</label>
                        <Input
                          value={features.features_section_title || ''}
                          onChange={(e) => setFeatures(prev => prev ? { ...prev, features_section_title: e.target.value } : prev)}
                          placeholder="Featured Services"
                          className="text-sm"
                        />
                      </div>

                      {/* Layout Selection */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Layout</label>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => setFeatures(prev => prev ? { ...prev, features_layout: 'rows' } : prev)}
                            className={`px-3 py-1 text-sm rounded ${
                              features.features_layout !== 'columns' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'
                            }`}
                          >
                            Rows
                          </button>
                          <button
                            type="button"
                            onClick={() => setFeatures(prev => prev ? { ...prev, features_layout: 'columns' } : prev)}
                            className={`px-3 py-1 text-sm rounded ${
                              features.features_layout === 'columns' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'
                            }`}
                          >
                            Columns
                          </button>
                        </div>
                      </div>

                      {/* Feature 1 */}
                      <div className="p-3 border border-gray-200 rounded-lg bg-white">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">Feature 1</span>
                          <button
                            type="button"
                            onClick={() => setFeatures(prev => prev ? { ...prev, feature1_enabled: !prev.feature1_enabled } : prev)}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                              features.feature1_enabled ? 'bg-blue-600' : 'bg-gray-300'
                            }`}
                          >
                            <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                              features.feature1_enabled ? 'translate-x-5' : 'translate-x-1'
                            }`} />
                          </button>
                        </div>
                        <Input
                          value={features.feature1_title || ''}
                          onChange={(e) => setFeatures(prev => prev ? { ...prev, feature1_title: e.target.value } : prev)}
                          placeholder="Feature title"
                          className="text-sm mb-2"
                        />
                        <Textarea
                          value={features.feature1_description || ''}
                          onChange={(e) => setFeatures(prev => prev ? { ...prev, feature1_description: e.target.value } : prev)}
                          placeholder="Feature description"
                          className="text-sm"
                          rows={2}
                        />
                        <div className="mt-2 flex items-center gap-3">
                          <img
                            src={featureImagePreviews.feature1 || features.feature1_image || professional.avatar_url || '/bally-Market-sign-200px.png'}
                            alt="Feature 1"
                            className="w-12 h-12 rounded object-cover bg-gray-100"
                          />
                          <label
                            htmlFor="feature1-image-pro"
                            className="cursor-pointer bg-blue-50 text-blue-600 px-3 py-1.5 rounded text-xs hover:bg-blue-100 flex items-center gap-1"
                          >
                            <Upload className="w-3 h-3" />
                            <span>Upload image</span>
                          </label>
                          <input
                            id="feature1-image-pro"
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              if (file.size > 5 * 1024 * 1024) {
                                alert('File size must be less than 5MB');
                                return;
                              }
                              setFeatureImageFiles(prev => ({ ...prev, feature1: file }));
                              setFeatureImagePreviews(prev => ({ ...prev, feature1: URL.createObjectURL(file) }));
                            }}
                          />
                        </div>
                      </div>

                      {/* Feature 2 */}
                      <div className="p-3 border border-gray-200 rounded-lg bg-white">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">Feature 2</span>
                          <button
                            type="button"
                            onClick={() => setFeatures(prev => prev ? { ...prev, feature2_enabled: !prev.feature2_enabled } : prev)}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                              features.feature2_enabled ? 'bg-blue-600' : 'bg-gray-300'
                            }`}
                          >
                            <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                              features.feature2_enabled ? 'translate-x-5' : 'translate-x-1'
                            }`} />
                          </button>
                        </div>
                        <Input
                          value={features.feature2_title || ''}
                          onChange={(e) => setFeatures(prev => prev ? { ...prev, feature2_title: e.target.value } : prev)}
                          placeholder="Feature title"
                          className="text-sm mb-2"
                        />
                        <Textarea
                          value={features.feature2_description || ''}
                          onChange={(e) => setFeatures(prev => prev ? { ...prev, feature2_description: e.target.value } : prev)}
                          placeholder="Feature description"
                          className="text-sm"
                          rows={2}
                        />
                        <div className="mt-2 flex items-center gap-3">
                          <img
                            src={featureImagePreviews.feature2 || features.feature2_image || professional.avatar_url || '/bally-Market-sign-200px.png'}
                            alt="Feature 2"
                            className="w-12 h-12 rounded object-cover bg-gray-100"
                          />
                          <label
                            htmlFor="feature2-image-pro"
                            className="cursor-pointer bg-blue-50 text-blue-600 px-3 py-1.5 rounded text-xs hover:bg-blue-100 flex items-center gap-1"
                          >
                            <Upload className="w-3 h-3" />
                            <span>Upload image</span>
                          </label>
                          <input
                            id="feature2-image-pro"
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              if (file.size > 5 * 1024 * 1024) {
                                alert('File size must be less than 5MB');
                                return;
                              }
                              setFeatureImageFiles(prev => ({ ...prev, feature2: file }));
                              setFeatureImagePreviews(prev => ({ ...prev, feature2: URL.createObjectURL(file) }));
                            }}
                          />
                        </div>
                      </div>

                      {/* Feature 3 */}
                      <div className="p-3 border border-gray-200 rounded-lg bg-white">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">Feature 3</span>
                          <button
                            type="button"
                            onClick={() => setFeatures(prev => prev ? { ...prev, feature3_enabled: !prev.feature3_enabled } : prev)}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                              features.feature3_enabled ? 'bg-blue-600' : 'bg-gray-300'
                            }`}
                          >
                            <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                              features.feature3_enabled ? 'translate-x-5' : 'translate-x-1'
                            }`} />
                          </button>
                        </div>
                        <Input
                          value={features.feature3_title || ''}
                          onChange={(e) => setFeatures(prev => prev ? { ...prev, feature3_title: e.target.value } : prev)}
                          placeholder="Feature title"
                          className="text-sm mb-2"
                        />
                        <Textarea
                          value={features.feature3_description || ''}
                          onChange={(e) => setFeatures(prev => prev ? { ...prev, feature3_description: e.target.value } : prev)}
                          placeholder="Feature description"
                          className="text-sm"
                          rows={2}
                        />
                        <div className="mt-2 flex items-center gap-3">
                          <img
                            src={featureImagePreviews.feature3 || features.feature3_image || professional.avatar_url || '/bally-Market-sign-200px.png'}
                            alt="Feature 3"
                            className="w-12 h-12 rounded object-cover bg-gray-100"
                          />
                          <label
                            htmlFor="feature3-image-pro"
                            className="cursor-pointer bg-blue-50 text-blue-600 px-3 py-1.5 rounded text-xs hover:bg-blue-100 flex items-center gap-1"
                          >
                            <Upload className="w-3 h-3" />
                            <span>Upload image</span>
                          </label>
                          <input
                            id="feature3-image-pro"
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              if (file.size > 5 * 1024 * 1024) {
                                alert('File size must be less than 5MB');
                                return;
                              }
                              setFeatureImageFiles(prev => ({ ...prev, feature3: file }));
                              setFeatureImagePreviews(prev => ({ ...prev, feature3: URL.createObjectURL(file) }));
                            }}
                          />
                        </div>
                      </div>
                      
                      {/* Save features button */}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleSaveFeatures}
                        disabled={savingFeatures}
                        className="w-full"
                      >
                        {savingFeatures ? (
                          <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving...</>
                        ) : (
                          'Save Features'
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Info for mockup/unclaimed entries without user_id */}
              {!professional.user_id && (
                <div className="pt-4 border-t border-gray-200">
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Unclaimed Business:</strong> Social links and features will become available 
                      once a real owner registers and claims this listing. Basic info can still be edited above.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {error && (
              <div className="text-sm text-red-600">{error}</div>
            )}

            <div className="flex justify-end space-x-4 mt-8">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="px-4 py-2"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="px-4 py-2"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center space-x-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Saving...</span>
                  </div>
                ) : (
                  <span>{professional?.id ? 'Save Changes' : 'Add Professional'}</span>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditProfessionalForm;
